/* eslint-disable react/no-unescaped-entities */
/**
 * NegociationNouveau.jsx — Création d'un dossier de négociation (4 étapes).
 *
 * Charte stricte (`/app/memory/NEGOCIATION_CHARTER.md`).
 */
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { apiClient, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowLeft, ArrowRight, Loader2, ShieldCheck, CheckCircle2, Users, FileText, Mail, Sparkles,
} from "lucide-react";

const ROLES = [
  { key: "creancier",    label: "Créancier" },
  { key: "debiteur",     label: "Débiteur" },
  { key: "fournisseur",  label: "Fournisseur" },
  { key: "client",       label: "Client" },
  { key: "bailleur",     label: "Bailleur" },
  { key: "locataire",    label: "Locataire" },
  { key: "prestataire",  label: "Prestataire" },
  { key: "maitre_ouvrage", label: "Maître d'ouvrage" },
  { key: "autre",        label: "Autre" },
];

const COMPLIANCE = (
  "Confilex est un outil de structuration documentaire et de formalisation " +
  "d'informations fournies par les utilisateurs. Confilex ne fournit aucune " +
  "consultation juridique, n'analyse pas les droits des parties, ne vérifie " +
  "pas la validité des créances et ne rédige aucun acte juridique."
);

export default function NegociationNouveau() {
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    initiator_party: { role: "creancier", name: "", organization: "", email: "", phone: "" },
    opposing_party:  { role: "debiteur",  name: "", organization: "", email: "", phone: "" },
    subject: "",
    description: "",
    amount_eur: "",
    requested_schedule: "",
  });

  const setParty = (which, field, val) =>
    setForm(f => ({ ...f, [which]: { ...f[which], [field]: val } }));

  const canNext = (s) => {
    if (s === 1) return form.initiator_party.name.trim() && form.opposing_party.name.trim();
    if (s === 2) return form.subject.trim().length >= 4;
    if (s === 3) return true;
    return true;
  };

  const submit = async () => {
    setBusy(true);
    try {
      const payload = {
        ...form,
        amount_eur: form.amount_eur === "" ? null : Number(form.amount_eur),
      };
      const { data } = await apiClient.post("/negotiation/cases", payload);
      toast.success("Dossier ouvert", {
        description: data.email?.mocked
          ? "Invitation préparée (envoi email mocké en dev)"
          : "Email d'invitation envoyé à la partie destinataire.",
      });
      nav(`/negociation/dossier/${data.case.id}`);
    } catch (e) {
      toast.error("Création impossible", { description: formatApiError(e?.response?.data?.detail) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAF7]">
      <Navbar variant="light" />
      <main className="max-w-4xl mx-auto px-6 lg:px-10 py-10" data-testid="negociation-nouveau">
        <Link to="/negociation" className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-[#0B3A2A] mb-4" data-testid="back-landing">
          <ArrowLeft className="w-3.5 h-3.5" /> Retour à CONFILEX Négociation
        </Link>
        <p className="text-[11px] uppercase tracking-[0.3em] text-[#C9A961] font-medium mb-2">Création d'un dossier</p>
        <h1 className="font-serif text-3xl md:text-4xl text-[#0B3A2A] tracking-tight">
          Structurez votre dossier de négociation
        </h1>
        <p className="mt-2 text-sm text-slate-600">
          4 étapes pour préparer une démarche amiable, sans engagement juridique.
        </p>

        {/* Mention de conformité — affichée dès l'ouverture du wizard (charte) */}
        <div className="mt-6 p-4 rounded-lg bg-[#FAF7F1] border border-[#C9A961]/40 text-xs text-[#0B3A2A] flex items-start gap-2" data-testid="compliance-notice-top">
          <ShieldCheck className="w-4 h-4 text-[#C9A961] mt-0.5 shrink-0" />
          <p><b>Mention de conformité :</b> {COMPLIANCE}</p>
        </div>

        {/* Stepper */}
        <ol className="mt-8 flex flex-wrap items-center gap-2 text-[11px] uppercase tracking-wider" data-testid="stepper">
          {[
            { n: 1, label: "Parties", Icon: Users },
            { n: 2, label: "Objet & montant", Icon: FileText },
            { n: 3, label: "Échéancier souhaité", Icon: Sparkles },
            { n: 4, label: "Confirmation", Icon: Mail },
          ].map(({ n, label, Icon }) => (
            <li key={n} className={`flex items-center gap-2 px-3 py-1.5 rounded-full border ${step === n ? "bg-[#0B3A2A] text-white border-[#0B3A2A]" : step > n ? "bg-[#C9A961]/20 text-[#0B3A2A] border-[#C9A961]/40" : "bg-white text-slate-500 border-slate-200"}`}>
              <Icon className="w-3.5 h-3.5" /> {n}. {label}
            </li>
          ))}
        </ol>

        <section className="mt-8 rounded-2xl bg-white border border-slate-200 p-7">
          {/* STEP 1 — Parties */}
          {step === 1 && (
            <div className="space-y-6" data-testid="step-1">
              <h2 className="font-serif text-xl text-[#0B3A2A]">Identité des parties</h2>
              <div className="grid md:grid-cols-2 gap-6">
                <PartyBlock title="Vous (partie initiatrice)" testidPrefix="initiator"
                  party={form.initiator_party} onChange={(f, v) => setParty("initiator_party", f, v)} />
                <PartyBlock title="Partie destinataire" testidPrefix="opposing"
                  party={form.opposing_party} onChange={(f, v) => setParty("opposing_party", f, v)}
                  hint="Un email permettra l'envoi automatique de l'invitation positive." />
              </div>
            </div>
          )}

          {/* STEP 2 — Objet */}
          {step === 2 && (
            <div className="space-y-4" data-testid="step-2">
              <h2 className="font-serif text-xl text-[#0B3A2A]">Objet du dossier</h2>
              <Field label="Objet (résumé en une ligne) *">
                <input value={form.subject} onChange={e => setForm(f => ({ ...f, subject: e.target.value }))}
                  data-testid="field-subject" maxLength={200}
                  placeholder="Ex : Facture impayée n°2025-0142 — échéancier proposé"
                  className="w-full px-3 py-2 rounded-lg border border-slate-300 focus:border-[#C9A961] focus:ring-2 focus:ring-[#C9A961]/30 outline-none" />
              </Field>
              <Field label="Description détaillée (optionnel)">
                <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                  data-testid="field-description" rows={6} maxLength={4000}
                  placeholder="Contexte, historique des échanges, éléments importants à signaler à la partie destinataire."
                  className="w-full px-3 py-2 rounded-lg border border-slate-300 focus:border-[#C9A961] focus:ring-2 focus:ring-[#C9A961]/30 outline-none resize-y" />
              </Field>
              <Field label="Montant concerné en € (optionnel)">
                <input type="number" min="0" step="0.01" value={form.amount_eur}
                  onChange={e => setForm(f => ({ ...f, amount_eur: e.target.value }))}
                  data-testid="field-amount"
                  placeholder="Ex : 4500.00"
                  className="w-full px-3 py-2 rounded-lg border border-slate-300 focus:border-[#C9A961] focus:ring-2 focus:ring-[#C9A961]/30 outline-none" />
              </Field>
            </div>
          )}

          {/* STEP 3 — Échéancier souhaité */}
          {step === 3 && (
            <div className="space-y-4" data-testid="step-3">
              <h2 className="font-serif text-xl text-[#0B3A2A]">Échéancier souhaité</h2>
              <p className="text-sm text-slate-600">
                Décrivez librement la solution amiable que vous proposez (délai, fractionnement, remise commerciale…).
                Ce texte sera intégré tel quel dans la première proposition formalisée.
              </p>
              <Field label="Échéancier ou conditions proposées (optionnel)">
                <textarea value={form.requested_schedule}
                  onChange={e => setForm(f => ({ ...f, requested_schedule: e.target.value }))}
                  data-testid="field-schedule" rows={5} maxLength={600}
                  placeholder="Ex : 3 versements de 1500 € le 5 de chaque mois à partir du mois prochain, sans pénalités."
                  className="w-full px-3 py-2 rounded-lg border border-slate-300 focus:border-[#C9A961] focus:ring-2 focus:ring-[#C9A961]/30 outline-none resize-y" />
              </Field>
              <div className="p-4 rounded-lg bg-[#FAF7F1] border border-[#C9A961]/30 text-xs text-[#0B3A2A] flex items-start gap-2">
                <ShieldCheck className="w-4 h-4 text-[#C9A961] mt-0.5 shrink-0" />
                <p>
                  Le texte saisi sera repris comme « conditions déclarées » dans la synthèse PDF.
                  Aucun engagement juridique n'en découle — il s'agit d'une proposition de règlement
                  ouverte à la discussion.
                </p>
              </div>
            </div>
          )}

          {/* STEP 4 — Confirmation */}
          {step === 4 && (
            <div className="space-y-5" data-testid="step-4">
              <h2 className="font-serif text-xl text-[#0B3A2A]">Récapitulatif avant ouverture</h2>
              <div className="grid md:grid-cols-2 gap-4 text-sm">
                <SummaryCard title="Partie initiatrice" party={form.initiator_party} />
                <SummaryCard title="Partie destinataire" party={form.opposing_party} />
              </div>
              <div className="rounded-lg bg-[#FAFAF7] border border-slate-200 p-4 text-sm">
                <p className="font-medium text-[#0B3A2A]">{form.subject || "—"}</p>
                {form.description && <p className="mt-2 text-slate-600 whitespace-pre-wrap">{form.description}</p>}
                <div className="mt-3 grid sm:grid-cols-2 gap-3 text-xs text-slate-500">
                  {form.amount_eur && <div><b>Montant :</b> {Number(form.amount_eur).toLocaleString("fr-FR", { style: "currency", currency: "EUR" })}</div>}
                  {form.requested_schedule && <div><b>Échéancier proposé :</b> {form.requested_schedule}</div>}
                </div>
              </div>

              <div className="p-4 rounded-lg bg-[#FAF7F1] border border-[#C9A961]/40 text-xs text-[#0B3A2A] flex items-start gap-2">
                <ShieldCheck className="w-4 h-4 text-[#C9A961] mt-0.5 shrink-0" />
                <p><b>Mention de conformité :</b> {COMPLIANCE}</p>
              </div>
            </div>
          )}

          {/* Navigation */}
          <div className="mt-8 flex items-center justify-between gap-3 pt-6 border-t border-slate-200">
            <Button variant="outline" disabled={step === 1 || busy} onClick={() => setStep(s => s - 1)} data-testid="btn-prev">
              <ArrowLeft className="w-4 h-4 mr-1.5" /> Précédent
            </Button>
            {step < 4 ? (
              <Button disabled={!canNext(step) || busy} onClick={() => setStep(s => s + 1)}
                className="bg-[#0B3A2A] hover:bg-[#0B3A2A] text-white" data-testid="btn-next">
                Suivant <ArrowRight className="w-4 h-4 ml-1.5" />
              </Button>
            ) : (
              <Button disabled={busy} onClick={submit}
                className="bg-[#C9A961] hover:bg-[#D4B478] text-[#0B3A2A]" data-testid="btn-submit">
                {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-1.5" />}
                Ouvrir le dossier &amp; envoyer l'invitation
              </Button>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}

function PartyBlock({ title, party, onChange, testidPrefix, hint }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-wider text-[#C9A961] font-medium mb-3">{title}</p>
      <Field label="Rôle déclaré">
        <select value={party.role} onChange={e => onChange("role", e.target.value)}
          data-testid={`${testidPrefix}-role`}
          className="w-full px-3 py-2 rounded-lg border border-slate-300 focus:border-[#C9A961] focus:ring-2 focus:ring-[#C9A961]/30 outline-none bg-white">
          {ROLES.map(r => <option key={r.key} value={r.key}>{r.label}</option>)}
        </select>
      </Field>
      <Field label="Nom complet ou raison sociale *">
        <input value={party.name} onChange={e => onChange("name", e.target.value)}
          data-testid={`${testidPrefix}-name`} maxLength={200}
          className="w-full px-3 py-2 rounded-lg border border-slate-300 focus:border-[#C9A961] focus:ring-2 focus:ring-[#C9A961]/30 outline-none" />
      </Field>
      <Field label="Organisation / société (optionnel)">
        <input value={party.organization} onChange={e => onChange("organization", e.target.value)}
          data-testid={`${testidPrefix}-org`} maxLength={200}
          className="w-full px-3 py-2 rounded-lg border border-slate-300 focus:border-[#C9A961] focus:ring-2 focus:ring-[#C9A961]/30 outline-none" />
      </Field>
      <Field label="Email">
        <input type="email" value={party.email} onChange={e => onChange("email", e.target.value)}
          data-testid={`${testidPrefix}-email`}
          className="w-full px-3 py-2 rounded-lg border border-slate-300 focus:border-[#C9A961] focus:ring-2 focus:ring-[#C9A961]/30 outline-none" />
      </Field>
      {hint && <p className="text-[11px] text-slate-500 mt-1">{hint}</p>}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block mb-3">
      <span className="block text-xs text-slate-600 mb-1.5">{label}</span>
      {children}
    </label>
  );
}

function SummaryCard({ title, party }) {
  return (
    <div className="rounded-lg bg-[#FAFAF7] border border-slate-200 p-4">
      <p className="text-[10px] uppercase tracking-wider text-[#C9A961] font-medium mb-2">{title}</p>
      <p className="font-medium text-[#0B3A2A]">{party.name || "—"}</p>
      {party.organization && <p className="text-xs text-slate-500">{party.organization}</p>}
      <p className="text-[11px] text-slate-500 mt-1">
        Rôle : {ROLES.find(r => r.key === party.role)?.label || party.role}
      </p>
      {party.email && <p className="text-[11px] text-slate-500 mt-0.5">{party.email}</p>}
    </div>
  );
}
